import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";

/**
 * Panel de control de recordatorios.
 *
 * Dos pestañas:
 *  - Pendientes: cuotas que serían notificadas hoy según la configuración (preview).
 *  - Historial: registro de notificaciones enviadas (lectura del JSONL del backend).
 *
 * Permite ejecutar manualmente los envíos (real o simulado).
 */
const RecordatoriosControl = () => {
  const [tab, setTab] = useState("pendientes");

  const [pendientes, setPendientes] = useState([]);
  const [loadingPend, setLoadingPend] = useState(false);
  const [errorPend, setErrorPend] = useState("");

  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [errorHist, setErrorHist] = useState("");

  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState("");
  const [runError, setRunError] = useState("");

  const [preview, setPreview] = useState(null); // item seleccionado para mostrar cuerpo

  // Fecha contra la que se calculan los días de aviso. Vacío = hoy. Sirve para
  // recuperar días en los que el envío falló (los días de aviso son relativos
  // a "hoy", así que sin esto esas cuotas ya no vuelven a caer en la ventana).
  //
  // `fecha` es lo que se está tipeando y `fechaAplicada` lo que realmente se
  // consulta. Hacen falta las dos: al tipear el año en un <input type="date">
  // el navegador emite un valor completo por cada dígito (escribir "2" da el
  // año 0002), así que sin debounce se dispara una consulta por tecla y no se
  // llega a terminar de escribir el año.
  const [fecha, setFecha] = useState("");
  const [fechaAplicada, setFechaAplicada] = useState("");

  const fechaCompleta = (v) => !v || Number(String(v).slice(0, 4)) >= 2000;

  const userCompanyDb = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
    return user?.CompanyDB;
  }, []);

  const loadPendientes = async () => {
    setLoadingPend(true);
    setErrorPend("");
    try {
      const res = await apiClient.get("/recordatorios/pendientes", {
        params: { CompanyDB: userCompanyDb, ...(fechaAplicada ? { fecha: fechaAplicada } : {}) },
      });
      setPendientes(res?.data?.items || []);
    } catch (e) {
      setErrorPend(e?.response?.data?.error || e?.message || "Error cargando pendientes");
    } finally {
      setLoadingPend(false);
    }
  };

  const loadHistorial = async () => {
    setLoadingHist(true);
    setErrorHist("");
    try {
      const res = await apiClient.get("/recordatorios/historial", {
        params: { CompanyDB: userCompanyDb, limit: 500 },
      });
      setHistorial(res?.data?.items || []);
    } catch (e) {
      setErrorHist(e?.response?.data?.error || e?.message || "Error cargando historial");
    } finally {
      setLoadingHist(false);
    }
  };

  // Espera a que el usuario termine de tipear antes de aplicar la fecha, e
  // ignora los años incompletos que emite el input mientras se escribe.
  useEffect(() => {
    if (!fechaCompleta(fecha)) return undefined;
    const t = setTimeout(() => setFechaAplicada(fecha), 700);
    return () => clearTimeout(t);
  }, [fecha]);

  useEffect(() => {
    if (tab === "pendientes") loadPendientes();
    else loadHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fechaAplicada]);

  const runEnvios = async (dryRun) => {
    setRunning(true);
    setRunMessage("");
    setRunError("");
    try {
      const res = await apiClient.post(
        "/recordatorios/ejecutar",
        {},
        {
          params: {
            CompanyDB: userCompanyDb,
            dry_run: dryRun ? "true" : "false",
            ...(fechaAplicada ? { fecha: fechaAplicada } : {}),
          },
        }
      );
      const r = res?.data || {};
      setRunMessage(
        `${dryRun ? "Simulación" : "Ejecución"} completa` +
          (r.fecha_calculo ? ` (calculado al ${r.fecha_calculo})` : "") +
          `: ${r.total} pendientes, ` +
          `${r.enviados} enviados, ${r.omitidos_sin_email} sin email, ${r.errores} errores.`
      );
      if (!dryRun) loadHistorial();
      loadPendientes();
    } catch (e) {
      setRunError(e?.response?.data?.error || e?.message || "Error ejecutando envíos");
    } finally {
      setRunning(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const fmtFecha = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = String(iso).split("T")[0].split("-");
      if (y && m && d) return `${d}/${m}/${y}`;
    } catch {
      /* ignore */
    }
    return iso;
  };

  const totales = useMemo(() => {
    const prox = pendientes.filter((p) => p.tipo === "previo").length;
    const venc = pendientes.filter(
      (p) => p.tipo === "vencida" || p.tipo === "vencida_multiple"
    ).length;
    const sinEmail = pendientes.filter((p) => !p.email).length;
    return { prox, venc, sinEmail };
  }, [pendientes]);

  const esVencida = (p) => p.tipo === "vencida" || p.tipo === "vencida_multiple";

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h3 className="card-title mb-2 text-center">Recordatorios de cuotas - Control</h3>
        <hr className="mt-0 mb-3" />

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${tab === "pendientes" ? "active" : ""}`}
              onClick={() => setTab("pendientes")}
              type="button"
            >
              Pendientes ({pendientes.length})
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${tab === "historial" ? "active" : ""}`}
              onClick={() => setTab("historial")}
              type="button"
            >
              Historial
            </button>
          </li>
        </ul>

        {runMessage && <div className="alert alert-success">{runMessage}</div>}
        {runError && <div className="alert alert-danger">{runError}</div>}

        {tab === "pendientes" && (
          <>
            <div className="row g-2 align-items-end mb-3">
              <div className="col-md-4">
                <label className="form-label mb-1 small">
                  Calcular como si fuera la fecha
                </label>
                <div className="input-group input-group-sm">
                  <input
                    type="date"
                    className="form-control"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    min="2000-01-01"
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      setFecha("");
                      setFechaAplicada("");
                    }}
                    disabled={!fecha || running}
                    title="Volver a hoy"
                  >
                    Hoy
                  </button>
                </div>
              </div>
              <div className="col-md-8">
                {fecha !== fechaAplicada ? (
                  <div className="text-muted small">Terminá de escribir la fecha...</div>
                ) : fechaAplicada ? (
                  <div className="alert alert-warning py-2 mb-0 small">
                    <strong>Modo recuperación:</strong> se calcula con los días de aviso
                    relativos al <strong>{fechaAplicada}</strong>, no a hoy. Usalo para
                    reenviar días en los que el envío falló. Simulá antes de enviar.
                  </div>
                ) : (
                  <div className="text-muted small">
                    Dejá la fecha vacía para calcular con el día de hoy.
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <span className="badge bg-info">A vencer: {totales.prox}</span>
              <span className="badge bg-warning text-dark">Vencidas: {totales.venc}</span>
              <span className="badge bg-secondary">Sin email: {totales.sinEmail}</span>
              <div className="ms-auto d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={loadPendientes}
                  disabled={loadingPend || running}
                >
                  Refrescar
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => runEnvios(true)}
                  disabled={running || loadingPend || fecha !== fechaAplicada}
                >
                  {running ? "Procesando..." : "Simular envío"}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => runEnvios(false)}
                  disabled={
                    running || loadingPend || pendientes.length === 0 || fecha !== fechaAplicada
                  }
                >
                  Enviar ahora
                </button>
              </div>
            </div>

            {errorPend && <div className="alert alert-danger">{errorPend}</div>}
            {loadingPend ? (
              <div>Calculando pendientes...</div>
            ) : pendientes.length === 0 ? (
              <div className="alert alert-info">No hay cuotas para notificar hoy.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Contrato</th>
                      <th>Cuota</th>
                      <th>Vencimiento</th>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Monto</th>
                      <th>Días</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendientes.map((p, idx) => (
                      <tr key={`${p.doc_entry}-${p.nro_cuota}-${idx}`}>
                        <td>
                          <span className={`badge ${esVencida(p) ? "bg-warning text-dark" : "bg-info"}`}>
                            {p.tipo === "vencida_multiple"
                              ? `Vencidas (${p.cantidad_cuotas})`
                              : p.tipo === "vencida"
                              ? "Vencida"
                              : "A vencer"}
                          </span>
                        </td>
                        <td>{p.doc_num}</td>
                        <td>{p.nro_cuota}</td>
                        <td>{fmtFecha(p.fecha_vencimiento)}</td>
                        <td>
                          {p.card_name}
                          <div className="small text-muted">{p.card_code}</div>
                        </td>
                        <td>
                          {p.email || (
                            <span className="text-danger small">sin email</span>
                          )}
                        </td>
                        <td className="text-end">
                          {p.monto} {p.moneda}
                        </td>
                        <td>{esVencida(p) ? `+${p.dias_vencida}` : `-${p.dias_para_vencer}`}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setPreview(p)}
                            type="button"
                          >
                            Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "historial" && (
          <>
            <div className="d-flex justify-content-end mb-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={loadHistorial}
                disabled={loadingHist}
              >
                Refrescar
              </button>
            </div>
            {errorHist && <div className="alert alert-danger">{errorHist}</div>}
            {loadingHist ? (
              <div>Cargando historial...</div>
            ) : historial.length === 0 ? (
              <div className="alert alert-info">Sin registros.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Disparo</th>
                      <th>Contrato</th>
                      <th>Cuota</th>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Asunto</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((h, idx) => (
                      <tr key={idx}>
                        <td className="small">{fmtDate(h.ts)}</td>
                        <td>{h.subtipo || h.tipo}</td>
                        <td>{h.trigger || "-"}</td>
                        <td>{h.doc_num || "-"}</td>
                        <td>{h.nro_cuota ?? "-"}</td>
                        <td>
                          {h.card_name || "-"}
                          {h.card_code ? <div className="small text-muted">{h.card_code}</div> : null}
                        </td>
                        <td>{h.destinatario || "-"}</td>
                        <td className="small">{h.asunto || h.mensaje || "-"}</td>
                        <td>
                          <span
                            className={
                              "badge " +
                              (h.estado === "enviado" || h.estado === "ok"
                                ? "bg-success"
                                : h.estado === "error" || h.estado === "error_smtp"
                                ? "bg-danger"
                                : h.estado === "omitido_sin_email"
                                ? "bg-secondary"
                                : "bg-info")
                            }
                          >
                            {h.estado || "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Modal simple de preview de un pendiente */}
        {preview && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setPreview(null)}
          >
            <div
              className="bg-white rounded shadow p-4"
              style={{ width: "min(720px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Vista previa de notificación</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setPreview(null)}>
                  Cerrar
                </button>
              </div>
              <hr />
              <div className="mb-2">
                <strong>Para:</strong> {preview.email || <em>(sin email)</em>}
              </div>
              <div className="mb-2">
                <strong>Asunto:</strong> {preview.asunto_render}
              </div>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  background: "#f8f9fa",
                  padding: 12,
                  borderRadius: 6,
                  fontFamily: "inherit",
                }}
              >
                {preview.cuerpo_render}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordatoriosControl;
